import {
  Container,
  Grid,
  LinearProgress,
  TextField,
  Tooltip,
} from '@mui/material';

import { GroupsContainer, OutlinedIconButton } from 'shared/components/styled';
import React, { Fragment, memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Root } from './styled';
import VirtualizedView, { RowGrid } from 'shared/components/VirtualizedView';
import GlobalResource from 'shared/components/GlobalResources';
import { columnTypes } from 'shared/components/tables';
import { debounce, uniq, uniqBy } from 'lodash';
import { searchToQuery } from 'shared/components/makeNav';
import { useHistory, useLocation, Route, Redirect } from 'react-router-dom';

import { Add as AddIcon } from '@mui/icons-material';
import Autocomplete from '@mui/lab/Autocomplete';
import { DetailPopup } from 'shared/components';
import ServiceCollectionDrawer from '../ServiceCollectionDrawer';
import ResetIcon from '@mui/icons-material/Restore';
import { SearchInputContainer } from 'shared/components/SearchInput';
import get from 'lodash/get';
import { tableCols } from './constants';
import useHash from 'shared/utils/useHash';
import { useServiceCollectionsQuery } from './useServiceCollectionsQuery';
import { useDonorsQuery } from './queries';
import { useUser } from 'shared/auth';
import { HomePage, PageTop, HomeButton, MainButton, ButtonSpacer } from './styled';
import GlobalResourceDropDown from 'shared/account/components/GlobalResourceDropDown';

const baseUrl = '/account/causeCategories';
const storageKey = 'serviceCollectionsUrl';

export default function ServiceCollections() {
  const user = useUser();
  const effectiveUser = user?.effectiveUser;
  const userId = effectiveUser?.attributes['custom:user_id'];
  const defaultUrl = userId && localStorage.getItem(`User${userId}.${storageKey}`);
  const currentUrl = window.location.pathname + window.location.search;

  return (
    <Route
      path={baseUrl}
      component={ServiceCollectionsMain}
    >
      {defaultUrl && defaultUrl !== currentUrl &&
        <Redirect to={defaultUrl}/>}
    </Route>
  );
}

function ServiceCollectionsMain () {
  const { data, isLoading, refresh } = useServiceCollectionsQuery();
  const { data: donors, isLoading: isLoadingDonors  } = useDonorsQuery();

  const user = useUser();
  const effectiveUser = user?.effectiveUser;
  const userId = effectiveUser?.attributes['custom:user_id'];
  const { search: searchParams, pathname } = useLocation();
  const [selection, setSelection] = useState([]);

  /* drawer requirements */
  const [selectedServiceCollection, setSelectedServiceCollection] = useState();
  const [drawerOpen, setDrawerOpen] = useState(false);
//  const drawerProps = { tags, services, benchmarkTypes, measurementTypes, stages }

  const handleSave = useCallback((values, resetForm) => {
    setSelectedServiceCollection();
    setDrawerOpen(false);
    refresh();
  }, [refresh]);

  const onClose = useCallback(() => {
    setSelectedServiceCollection();
    setDrawerOpen(false);
  }, []);

  const handleAddNew = useCallback(() => {
    setSelection();
    setSelectedServiceCollection();
    setDrawerOpen(true);
  }, []);

  const handleViewDetails = (item) => {
    setSelectedServiceCollection(item.dbId);
    setDrawerOpen(true);
  };

  const handleEdit = useCallback(() => {
    if (selection) {
      if (selection.length === 0) {
        setSelectedServiceCollection();
      } else if (selection.length === 1) {
        setSelectedServiceCollection(selection[0]?.dbId);
        setDrawerOpen(true);
      } else {
        alert('Cannot edit multiple items');
      }
    }
  }, [selection]);
  /* end of drawer requirements */  

  useEffect(() => {
    const key = `User${userId}.${storageKey}`;
    localStorage.setItem(key, `${pathname}${searchParams}`);
  }, [searchParams, pathname, userId]);
  const { push } = useHistory();
  const handleClearPageSettings = useCallback(() => {
    const key = `User${userId}.${storageKey}`;
    localStorage.removeItem(key);
    push(baseUrl);
  }, [userId, push]);

  const [filter, setFilter] = useState();

  const groups = data
    ?.filter(obj => !filter || matchQuickFilter(obj, filter));
  const [columns, setColumns] = useState(tableCols);
  const hash = useHash() || '';
  const hashParts = hash.substring(1).split('/').filter(part => part);

  const handleRenderRow = useCallback(({
    value,
    columns,
    style, // Style object to be applied to row (to position it)
    selectedRows,
    setSelectedRows,
    hasCheckBox,
    isLastRow,
  }) => {
    return (
      <RowGrid
        {...{
          style, value, columns, columnTypes,
          selectedRows, setSelectedRows, hasCheckBox,
          isLastRow,
        }}
        keyField="dbId"
      />
    );
  }, [columns]);

  const searchOptions = data && uniq(
    quickFilterFields
      .reduce((all, field) => [
        ...all,
        ...data.map(row => get(row, field))
      ],[])
  )
    .filter(option => option)
    .sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()));

  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const handleSearchFocus = useCallback(() => {
    setIsSearchFocused(true);
  }, []);
  const handleSearchBlur = useCallback(() => {
    setIsSearchFocused(false);
  }, []);
  const debouncedSetFilter = useMemo(() => debounce(setFilter, 500), []);
  const handleSearchInput = useCallback((event, value) => {
    debouncedSetFilter(value);
  }, [debouncedSetFilter]);
  const handleSearchRenderInput = useCallback(params => (
    <SearchInputContainer maxWidth={400}>
      <TextField
        placeholder={isSearchFocused ? undefined : 'Search Cause Categories'}
        onFocus={handleSearchFocus}
        onBlur={handleSearchBlur}
        {...params}
      />
    </SearchInputContainer>
  ), [handleSearchInput, isSearchFocused]);
  
  return (<Fragment>
    {/* <Rows
      {...{
        isLoading,
        columns,
        searchOptions, handleSearchInput, 
        handleSearchRenderInput, groups, 
        columns,
        handleClearPageSettings, refresh,
        handleRenderRow, setSelection,
        selection, handleAddNew, handleEdit
      }}
    /> */}
    <GlobalResource
      resource='Cause Category'
      fields={['dbId', 'name', 'description']}
      fieldTitles={['ID', 'Name', 'Description']}
      onAddNew={handleAddNew}
      {...{
        handleViewDetails, data, isLoading
      }}
    />
    <DetailPopup title="Cause Category" open={drawerOpen} onClose={onClose} insideCard={false} width={'450px'}>
      <Container maxWidth={false}>
        <ServiceCollectionDrawer onSave={handleSave} dbId={selectedServiceCollection} donors={donors}/>
      </Container>
    </DetailPopup>
  </Fragment>);
}

const Rows = memo(({
  isLoading, searchOptions, handleSearchInput, 
  handleSearchRenderInput, groups, 
  columns,
  handleClearPageSettings,
  handleRenderRow, setSelection,
  selection, handleAddNew, handleEdit
}) => {
  const [filters, setFilters] = useState([]);
  const handleFilterValue = useCallback(() => {
    try {
      const { search } = window.location;
      const query = searchToQuery(search);
      setFilters([
        'name', 'description',
      ]
        .filter(name => query[name])
        .map(name => [name, query[name]])
        .reduce((all, [name, values]) => [
          ...all,
          ...JSON.parse(values)
            .map(value => ({ filter: name, value }))
        ], [])
      );
    } catch (err) {
      alert(err);
    }
  }, []);
  const filtered = groups
    ?.filter(obj => !filters.length || filters
      .some(({ filter, value }) => get(obj, filter) === value));

  const virtualized = useRef();
  useEffect(() => {
    if (!virtualized) {
      return;
    }
    virtualized.current?.clearSelection();
  }, []);

  return (
    <HomePage noPadding fullHeight>
      <PageTop>
        {isLoading && <LinearProgress variant="query"/>}
        <Grid 
          container
          direction="row"
          alignItems="center"
          xs={12}
        >
          <Grid item xs={3}>
            <Autocomplete
              id="indicator-search"
              freeSolo
              options={searchOptions}
              renderInput={handleSearchRenderInput}
              onInputChange={handleSearchInput}
            />
          </Grid>
          <Grid item xs={6}>
            <Tooltip title="Reset filters" arrow>
              <OutlinedIconButton
                  onClick={handleClearPageSettings}
                >
                <ResetIcon/>
              </OutlinedIconButton>
            </Tooltip>
            <ButtonSpacer withBorder/>
            { selection && selection.length > 0 && (
              <HomeButton onClick={handleEdit} disabled={(selection.length > 1)}>
                Edit
              </HomeButton>
            )}
          </Grid>
          <Grid item xs={3} container justifyContent="flex-end">
            <MainButton
              onClick={handleAddNew}
              startIcon={<AddIcon/>}
            >
              Add Cause Category
            </MainButton>
          </Grid>
        </Grid>
        <GlobalResourceDropDown value="Cause Categories"/>
      </PageTop>
      <Root>
        <GroupsContainer>
          <VirtualizedView
            ref={virtualized}
            view="grid"
            groups={filtered}
            unfilteredGroups={groups}
            {...{ columns, isLoading }}
            onRenderRow={handleRenderRow}
            onSelectionChanged={setSelection}
            onFilterValue={handleFilterValue}
          />
        </GroupsContainer>
      </Root>
    </HomePage>
  );
});


const quickFilterFields = [
  'name',
  'description',
];

function matchQuickFilter(obj, filter) {
  filter = filter.toLowerCase();
  return quickFilterFields
    .some(field => String(get(obj, field)).toLowerCase().includes(filter));
}
